import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProjectDetail as ProjectDetailT, ProjectTask } from "../api";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const HEALTH: Record<string, string> = { ok: "#1f7a4d", warn: "#9a7b1f", bad: "#b3261e", active: "#2a6f8a", neutral: "#8a8e88" };
const TASK_TONE: Record<string, { bg: string; fg: string }> = {
  done: { bg: "#e8f4ee", fg: "#1f7a4d" },
  doing: { bg: "#e8eef7", fg: "#2a5f9e" },
  blocked: { bg: "#f7e4e0", fg: "#b3261e" },
  todo: { bg: "#f0efe9", fg: "#6b706a" },
};
type Tab = "tasks" | "time" | "updates" | "billing" | "people";
const CLIENT_ROLES = ["viewer", "contributor", "sponsor"] as const;
const CARD = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)" } as const;
const INVOICE_TONE: Record<string, { bg: string; fg: string }> = {
  sent: { bg: "#e8eef7", fg: "#2a5f9e" },
  paid: { bg: "#e8f4ee", fg: "#1f7a4d" },
  draft: { bg: "#f0efe9", fg: "#6b706a" },
  overdue: { bg: "#f7e4e0", fg: "#b3261e" },
};
const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function fmtDate(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
}

export function ProjectDetail() {
  const { projectId } = useParams({ from: "/projects/$projectId" });
  const [tab, setTab] = useState<Tab>("tasks");
  const { data, isLoading, error } = useQuery({ queryKey: ["project", projectId], queryFn: () => api.project(projectId) });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const card = CARD;

  return (
    <Page>
      <div>
        <Link to="/projects" style={{ fontSize: 12, color: "#8a8e88", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span aria-hidden>←</span>Projects
        </Link>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>{data.name}</h1>
          <span style={{ fontSize: 13, color: "#8a8e88" }}>{data.client}</span>
          <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 999, background: `${HEALTH[data.scheduleHealth]}18`, color: HEALTH[data.scheduleHealth] }}>{data.status}</span>
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: 12.5, color: "#8a8e88", flexWrap: "wrap" }}>
          <span>PM · {data.pm ?? "—"}</span>
          <span>Go-live · {fmtDate(data.goLiveDate)}</span>
          <span>Progress · {data.progressPct}%</span>
          {data.clientContacts.length > 0 && <span>Contacts · {data.clientContacts.join(", ")}</span>}
        </div>
      </div>

      {/* Warnings */}
      {data.warnings.length > 0 && (
        <div style={{ ...card, boxShadow: "inset 0 0 0 1px rgba(217,119,87,.4)", padding: "12px 16px" }}>
          {data.warnings.map((w, i) => (
            <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "5px 0", borderTop: i ? "1px solid #f4f3ee" : "none" }}>
              <Icon name="alert-triangle" size={14} color={w.level === "behind" ? "#b3261e" : "#c0612f"} />
              <span style={{ fontSize: 12.5, color: "#7a4a22" }}>{w.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #ececec" }}>
        {([["tasks", "Tasks"], ["time", `Time · ${data.time.billableHours}h billable`], ["updates", `Client updates · ${data.updates.length}`], ["billing", `Billing · ${data.billing.invoices.length}`], ["people", `People · ${data.members.length}`]] as [Tab, string][]).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ fontSize: 12.5, fontWeight: 600, border: 0, borderBottom: `2px solid ${tab === k ? "#006747" : "transparent"}`, background: "none", color: tab === k ? "#006747" : "#8a8e88", padding: "8px 12px", cursor: "pointer" }}>{label}</button>
        ))}
      </div>

      {tab === "tasks" && (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${data.lists.length}, minmax(0,1fr))`, gap: 12, alignItems: "start" }}>
          {data.lists.map((l) => (
            <div key={l.id} style={{ ...card, padding: "10px 12px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: "#a8aca6", marginBottom: 8 }}>{l.name} <span style={{ color: "#cfd2cb" }}>{l.tasks.length}</span></div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {l.tasks.map((t) => <TaskCard key={t.id} t={t} />)}
                {l.tasks.length === 0 && <div style={{ fontSize: 11.5, color: "#bcbfba" }}>—</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "time" && (
        <div style={{ ...card, padding: "6px 0" }}>
          <div style={{ display: "flex", gap: 18, padding: "10px 18px", borderBottom: "1px solid #f0efe9" }}>
            <span style={{ fontSize: 13 }}><b style={{ fontFamily: "'Space Mono',monospace" }}>{data.time.billableHours}h</b> <span style={{ color: "#8a8e88" }}>billable</span></span>
            <span style={{ fontSize: 13 }}><b style={{ fontFamily: "'Space Mono',monospace" }}>{data.time.totalHours}h</b> <span style={{ color: "#8a8e88" }}>total</span></span>
          </div>
          {data.time.entries.map((e) => (
            <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 18px", borderTop: "1px solid #f4f3ee", fontSize: 12.5 }}>
              <span style={{ fontWeight: 600, minWidth: 110 }}>{e.who}</span>
              <span style={{ flex: 1, color: "#8a8e88" }}>{e.note}</span>
              {e.billable ? <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 999, background: "#e8f4ee", color: "#1f7a4d" }}>billable</span> : <span style={{ fontSize: 10, color: "#a8aca6" }}>internal</span>}
              <span style={{ fontFamily: "'Space Mono',monospace", minWidth: 60, textAlign: "right" }}>{Math.round((e.minutes / 60) * 10) / 10}h</span>
            </div>
          ))}
        </div>
      )}

      {tab === "updates" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {data.updates.map((u) => (
            <div key={u.id} style={{ ...card, padding: "14px 18px" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{u.subject}</span>
                {u.aiDrafted && <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 600, color: "#006747", background: "#e8f4ee", borderRadius: 4, padding: "1px 6px" }}><Icon name="sparkles" size={10} color="#006747" />AI-drafted</span>}
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: "#8a8e88" }}>{fmtDate(u.sentAt)}</span>
              </div>
              <div style={{ fontSize: 12.5, color: "#4a504a", marginTop: 6, lineHeight: 1.55 }}>{u.body}</div>
              {u.approvedBy && <div style={{ fontSize: 11, color: "#a8aca6", marginTop: 6 }}>Approved by {u.approvedBy}</div>}
            </div>
          ))}
          {data.updates.length === 0 && <div style={{ ...card, padding: "14px 18px", fontSize: 12.5, color: "#8a8e88" }}>No client updates sent yet.</div>}
        </div>
      )}

      {tab === "billing" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {data.billing.rateCards.length > 0 && (
            <div style={{ ...CARD, padding: "12px 16px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "#a8aca6", marginBottom: 6 }}>Rate card</div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                {data.billing.rateCards.map((r, i) => (
                  <span key={`${r.who}-${i}`} style={{ fontSize: 12.5 }}>{r.who} <b style={{ fontFamily: "'Space Mono',monospace" }}>${r.hourlyRate}/h</b></span>
                ))}
              </div>
            </div>
          )}
          {data.billing.invoices.map((inv) => {
            const tone = INVOICE_TONE[inv.status] ?? { bg: "#f0efe9", fg: "#6b706a" };
            return (
              <div key={inv.id} style={{ ...card, overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderBottom: "1px solid #f0efe9" }}>
                  <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, color: "#8a8e88" }}>{inv.id.slice(0, 12)}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: tone.bg, color: tone.fg }}>{inv.status}</span>
                  <span style={{ fontSize: 11.5, color: "#8a8e88" }}>{inv.terms}</span>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Space Mono',monospace" }}>{money(inv.total)}</span>
                </div>
                {inv.lines.map((l, i) => (
                  <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 18px", borderTop: i ? "1px solid #f4f3ee" : "none", fontSize: 12.5 }}>
                    <span style={{ fontWeight: 600, minWidth: 110 }}>{l.who}</span>
                    <span style={{ flex: 1, color: "#8a8e88" }}>{l.description}</span>
                    <span style={{ fontFamily: "'Space Mono',monospace", color: "#a8aca6" }}>{l.hours}h × ${l.rate}</span>
                    <span style={{ fontFamily: "'Space Mono',monospace", minWidth: 80, textAlign: "right" }}>{money(l.amount)}</span>
                  </div>
                ))}
              </div>
            );
          })}
          {data.billing.invoices.length === 0 && <div style={{ ...card, padding: "14px 18px", fontSize: 12.5, color: "#8a8e88" }}>No invoices yet.</div>}
        </div>
      )}

      {tab === "people" && <PeoplePanel projectId={data.id} members={data.members} />}
    </Page>
  );
}

function PeoplePanel({ projectId, members }: { projectId: string; members: ProjectDetailT["members"] }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<(typeof CLIENT_ROLES)[number]>("viewer");
  const [err, setErr] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["project", projectId] });
  const invite = useMutation({
    mutationFn: () => api.inviteProjectMember(projectId, { name: name.trim(), email: email.trim(), role }),
    onSuccess: () => { setName(""); setEmail(""); setRole("viewer"); setErr(null); invalidate(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Could not invite"),
  });
  const remove = useMutation({
    mutationFn: (memberId: string) => api.removeProjectMember(projectId, memberId),
    onSuccess: invalidate,
  });

  const staff = members.filter((m) => m.side === "staff");
  const clients = members.filter((m) => m.side === "client");
  const canInvite = name.trim().length > 0 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) && !invite.isPending;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <MemberGroup title="Project team" hint="Recursive · staff" rows={staff} accent="#006747" soft="#eef4f1" />
      <MemberGroup title="Client contacts" hint="access is membership-only — removing a person here revokes their portal access" rows={clients} accent="#4a504a" soft="#f0efe9" onRemove={(id) => remove.mutate(id)} removing={remove.isPending} />

      {/* Invite client contact */}
      <div style={{ ...CARD, padding: "14px 16px" }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "#a8aca6", marginBottom: 10 }}>Invite client contact</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" style={inputSt} />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@client.org" style={inputSt} />
          <select value={role} onChange={(e) => setRole(e.target.value as (typeof CLIENT_ROLES)[number])} style={{ ...inputSt, flex: "none", width: 130 }}>
            {CLIENT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button
            onClick={() => canInvite && invite.mutate()}
            disabled={!canInvite}
            style={{ border: 0, borderRadius: 6, padding: "9px 16px", fontSize: 12.5, fontWeight: 600, background: canInvite ? "#006747" : "#f0efe9", color: canInvite ? "#fff" : "#8a8e88", cursor: canInvite ? "pointer" : "not-allowed" }}
          >
            {invite.isPending ? "Inviting…" : "Invite"}
          </button>
        </div>
        {err && <div style={{ fontSize: 12, color: "#b3261e", marginTop: 8 }}>{err}</div>}
        <div style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 10, lineHeight: 1.5 }}>
          Client roles: <b>viewer</b> reads the project, <b>contributor</b> can message the team, <b>sponsor</b> is the client lead. This is the only place project access is granted — the person's record shows it read-only.
        </div>
      </div>
    </div>
  );
}

const inputSt: React.CSSProperties = { flex: 1, minWidth: 140, padding: "9px 11px", border: "1px solid #dcdad2", borderRadius: 6, fontSize: 13, color: "#1d241f", outline: 0 };

function MemberGroup({ title, hint, rows, accent, soft, onRemove, removing }: { title: string; hint: string; rows: ProjectDetailT["members"]; accent: string; soft: string; onRemove?: (id: string) => void; removing?: boolean }) {
  return (
    <div style={{ ...CARD, padding: "12px 16px" }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "#a8aca6", marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 11.5, color: "#8a8e88", marginBottom: 10 }}>{hint}</div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "#bcbfba" }}>None yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((m) => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <span style={{ width: 28, height: 28, borderRadius: 3, background: soft, color: accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 700, flex: "none" }}>
                {m.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1d241f" }}>{m.name}</div>
                {m.email && <div style={{ fontSize: 11.5, color: "#8a8e88" }}>{m.email}</div>}
              </div>
              <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: soft, color: accent, textTransform: "capitalize" }}>{m.role}</span>
              {onRemove && (
                <button onClick={() => onRemove(m.id)} disabled={removing} title="Remove" style={{ border: 0, background: "none", cursor: removing ? "not-allowed" : "pointer", padding: 4, color: "#b3261e" }}>
                  <Icon name="x" size={15} color="#b3261e" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskCard({ t }: { t: ProjectTask }) {
  const tone = TASK_TONE[t.status] ?? { bg: "#f0efe9", fg: "#6b706a" };
  return (
    <div style={{ border: "1px solid #eceae3", borderRadius: 7, padding: "8px 10px", background: "#fff" }}>
      <div style={{ fontSize: 12.5, fontWeight: 500 }}>{t.title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 9.5, fontWeight: 600, padding: "1px 6px", borderRadius: 4, background: tone.bg, color: tone.fg }}>{t.status}</span>
        {t.assignee && <span style={{ fontSize: 10.5, color: "#8a8e88" }}>{t.assignee}</span>}
        {t.points != null && <span style={{ fontSize: 10, fontFamily: "'Space Mono',monospace", color: "#a8aca6" }}>{t.points}pt</span>}
        <div style={{ flex: 1 }} />
        {t.blockedForDays != null && <span style={{ fontSize: 10, color: "#b3261e" }}>blocked {t.blockedForDays}d</span>}
        {t.blockedForDays == null && t.dueInDays != null && <span style={{ fontSize: 10, color: t.dueInDays < 3 ? "#c0612f" : "#a8aca6" }}>{t.dueInDays <= 0 ? "due" : `${t.dueInDays}d`}</span>}
      </div>
    </div>
  );
}
